import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { List, Typography } from "@mui/material";
import { useQuery } from "@apollo/client";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StyledAccordion, StyledAccordionDetails, StyledAccordionSummary } from "../../../assets/css/styledComponent";
import { AvatarStyled, BookingTypeCard, NoBookingTypesMessage, NotFoundContainer, UserBookingTypePageContainer, UserDesc, UserInfo, Username } from "./userBookingTypePage.styles";
import { GET_BOOKING_TYPES, GET_STAFF_BY_URL } from "../../../graphql/queries";
import Error from "../../../components/common/error";
import BookingTypeListTemplate from "../../../components/business/bookingType/bookingTypeListTemplate";
import { getCategoryName } from "../../../util/utils";

const NotFound = () => {
  return (
    <NotFoundContainer>
      <h1>404 - Page not found</h1>
      <p>The requested page could not be found.</p>
    </NotFoundContainer>
  );
};

function UserBookingTypePage(props) {
  const { businessUrl, staffUrl } = useParams();
  const [expanded, setExpanded] = useState([true, true, true]);
  const handleChange = (panel) => (event, isExpanded) => {
    const newExpanded = [...expanded];
    newExpanded[panel] = isExpanded;
    setExpanded(newExpanded);
  };

  const { loading: staffLoading, error: staffError, data: staffDate } = useQuery(GET_STAFF_BY_URL, {
    variables: { staffUrl, businessUrl },
  });

  // Once staff data is fetched, extract the staff information
  const staff = staffDate?.staffByUrl;

  // Only trigger GET_BOOKING_TYPES once staff.id is available
  const { loading: userBookingTypesLoading, error: userBookingTypesError, data: userBookingTypesData } = useQuery(
    GET_BOOKING_TYPES,
    {
      variables: { staffId: Number(staff?.id) ?? -1 },
      skip: !staff?.id,  // Skip this query until staff.id is available
    }
  );

  // Loading state for both queries
  if (staffLoading || userBookingTypesLoading) return "Loading...";

  // Error state for both queries
  if (staffError || userBookingTypesError) return <Error />;

  const events = userBookingTypesData?.getBookingTypes?.filter(
    (obj) => obj.bookingTypeCategoryId === 1 && (obj.isPublic)
  );
  const services = userBookingTypesData?.getBookingTypes?.filter(
    (obj) => obj.bookingTypeCategoryId === 2 && (obj.isPublic)
  );
  const classes = userBookingTypesData?.getBookingTypes?.filter(
    (obj) => obj.bookingTypeCategoryId === 3 && (obj.isPublic)
  );

  const getBookingPath = (bookingType) => {
    return `/${businessUrl}/booking/${bookingType.link}`; // Dynamic path
  };

  return (
    <UserBookingTypePageContainer>
      {!staff ? (
        <NotFound />
      ) : (
        <>
          <BookingTypeCard>
            <UserInfo>
              <AvatarStyled src={staff?.image?.url} alt={staff?.user.name} />
              <Username>{staff?.user.username}</Username>
              <UserDesc>{staff?.user.desc}</UserDesc>
            </UserInfo>

            {(!events?.length && !services?.length && !classes?.length) && (
                <NoBookingTypesMessage>No public booking types available.</NoBookingTypesMessage>
            )}

            {events?.length > 0 && (
              <BookingTypeAccordion
                  title={getCategoryName(1)}
                data={events}
                expanded={expanded[0]} // Control the expanded state for the first accordion panel
                onChange={handleChange(0)} // Handle the change in the expanded state for the first category
                category={1}
                getBookingPath={getBookingPath}
              />
            )}

            {services?.length > 0 && (
              <BookingTypeAccordion
                  title={getCategoryName(2)}
                data={services}
                expanded={expanded[1]} // Control the expanded state for the second accordion panel
                onChange={handleChange(1)} // Handle the change in the expanded state for the second category
                category={2}
                getBookingPath={getBookingPath}
              />
            )}

            {classes?.length > 0 && (
              <BookingTypeAccordion
                  title={getCategoryName(3)}
                data={classes}
                expanded={expanded[2]} // Control the expanded state for the third accordion panel
                onChange={handleChange(2)} // Handle the change in the expanded state for the third category
                category={3}
                getBookingPath={getBookingPath}
              />
            )}
          </BookingTypeCard>
        </>
      )}
    </UserBookingTypePageContainer>
  );
}


function BookingTypeAccordion({ title, data, expanded, onChange, category, getBookingPath }) {

  return (
    <StyledAccordion expanded={expanded} onChange={onChange}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel-content" id="panel-header">
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#007bff' }}>
          {title}
        </Typography>
      </StyledAccordionSummary>

      <StyledAccordionDetails>
        <List>
          <BookingTypeListTemplate
            bookingTypes={data}
            category={category}
            embeded={null}
            getBookingPath={getBookingPath} // Pass the function as the prop
            ignoreDisplayOnBookingPageflag={true}
            showBookTypeColor={true}

          />
        </List>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
}


export default UserBookingTypePage;
